import { Drawer } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import { NavLinkConfig, NavMenuSection } from '../useNavigationItems'

import { Avatar, Icon } from '@percent/lemonade'
import classes from './MobileMenuDrawer.module.scss'

type MobileMenuDrawerProps = {
  opened: boolean
  onClose: () => void
  /** Destinations that did not fit in the bottom tab bar. */
  overflowLinks: NavLinkConfig[]
  menuSections: NavMenuSection[]
  accountName: string
  partnerLogo?: string | null
  partnerName: string
}

export function MobileMenuDrawer({
  opened,
  onClose,
  overflowLinks,
  menuSections,
  accountName,
  partnerLogo,
  partnerName
}: Readonly<MobileMenuDrawerProps>) {
  const { t } = useTranslation()

  const profileSection = menuSections.find(section => section.sectionKey === 'profile')
  const trailingSections = menuSections.filter(section => section.sectionKey !== 'profile')

  return (
    <Drawer
      classNames={{ content: classes.content, body: classes.body }}
      opened={opened}
      overlayProps={{ backgroundOpacity: 0.6, blur: 4, color: '#000' }}
      padding={0}
      position="left"
      size={310}
      title={null}
      withCloseButton={false}
      onClose={onClose}
    >
      <header className={classes.header}>
        {partnerLogo ? <img alt={`${partnerName} logo`} className={classes.logo} src={partnerLogo} /> : <span />}
        <button
          aria-label={t('workplace_giving.common.close')}
          className={classes.closeButton}
          data-testid="mobile-menu-close"
          type="button"
          onClick={onClose}
        >
          <Icon color="black" name="close" size={5} />
        </button>
      </header>

      <div className={classes.menu}>
        <div className={classes.primaryGroup}>
          {overflowLinks.map(link => (
            <NavLink
              key={link.key}
              className={({ isActive }) => (isActive ? `${classes.item} ${classes.itemActive}` : classes.item)}
              end={link.end}
              to={link.to}
              onClick={onClose}
            >
              {link.label}
            </NavLink>
          ))}

          {profileSection?.items.map(item => (
            <button
              key={item.key}
              className={classes.item}
              type="button"
              onClick={() => {
                item.onClick()
                onClose()
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className={classes.trailingGroup}>
          {trailingSections.map(section => (
            <div key={section.sectionKey} className={classes.section}>
              <hr className={classes.divider} />
              {section.items.map(item => (
                <button
                  key={item.key}
                  className={classes.item}
                  type="button"
                  onClick={() => {
                    item.onClick()
                    onClose()
                  }}
                >
                  {item.icon ? <Icon color="black" name={item.icon} size={5} /> : null}
                  <span className={classes.itemLabel}>{item.label}</span>
                </button>
              ))}
            </div>
          ))}

          <hr className={classes.divider} />
          <div className={classes.account}>
            <Avatar alt={accountName} shape="oval" size="small" />
            <span className={classes.accountName}>{accountName}</span>
          </div>
        </div>
      </div>
    </Drawer>
  )
}
